import React from "react"
import Table from "@mui/material/Table"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableCell from "@mui/material/TableCell"
import TableBody from "@mui/material/TableBody"
import Avatar from "@mui/material/Avatar"
import Skeleton from "@mui/material/Skeleton"
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-mui"
import i18n from "@/i18n"
import { CustomerContactSort, SortDirection, type GetCustomerContactsQuery } from "@/graphql/graphql"

const STYLES = {
  sortButtonBase: "w-full border-0 bg-transparent p-0 text-inherit",
  sortIconInactive: "ml-1 w-4 h-4 text-gray-400",
  sortIconActive: "ml-1 w-4 h-4",
  sortButtonName: "flex items-center cursor-pointer hover:text-blue-600",
  sortButtonPhone: "flex items-center cursor-pointer hover:text-blue-600",
  sortButtonRight: "flex items-center justify-end cursor-pointer hover:text-blue-600 text-right",
  headerCellRight: "text-right",
  tableWrapper: "p-4",
  skeletonAvatarCell: "w-0 pr-0",
  skeletonAvatar: "w-10 h-10",
  skeletonNameCell: "w-100",
  skeletonRightWrapper: "flex justify-end w-full",
  contactRow: "hover:bg-gray-50",
  contactAvatarCell: "w-0 pr-0",
  contactButton: "flex flex-col items-start border-0 bg-transparent p-0 text-left text-inherit hover:text-blue-600",
  contactName: "font-bold text-sm",
  contactEmail: "font-normal text-gray-700 text-sm",
  cellRight: "text-right",
} as const

const VIP_VISIT_THRESHOLD_PERCENT = 50

type Contact = GetCustomerContactsQuery["business"]["customerContacts"]["collection"][0]

const MissedReservations = ({ total, missed }: { total: number; missed: number }) => {
  if (total === 0) {
    return <div>-</div>
  }

  const percentage = Math.round((missed / total) * 100)

  return (
    <div className={percentage > VIP_VISIT_THRESHOLD_PERCENT ? "text-red-500" : ""}>
      {missed} ({percentage}%)
    </div>
  )
}

const TableHeader = ({
  sortBy,
  sortDirection,
  onSort,
}: {
  sortBy: CustomerContactSort | null
  sortDirection: SortDirection
  onSort: (column: CustomerContactSort) => void
}) => {
  const getSortIcon = (column: CustomerContactSort) => {
    if (sortBy !== column) {
      return <ArrowUpDown className={STYLES.sortIconInactive} />
    }
    return sortDirection === SortDirection.Desc ? (
      <ArrowDown className={STYLES.sortIconActive} />
    ) : (
      <ArrowUp className={STYLES.sortIconActive} />
    )
  }

  const renderSortButton = (column: CustomerContactSort, label: string, testId: string, className: string) => (
    <button
      type="button"
      data-testid={testId}
      className={`${className} ${STYLES.sortButtonBase}`}
      onClick={() => onSort(column)}
    >
      <span>{label}</span>
      {getSortIcon(column)}
    </button>
  )

  return (
    <TableHead>
      <TableRow>
        <TableCell colSpan={2}>
          {renderSortButton(
            CustomerContactSort.Name,
            i18n.t("app.clients.list.header.name"),
            "panel-clients-sort-name",
            STYLES.sortButtonName,
          )}
        </TableCell>
        <TableCell>
          {renderSortButton(
            CustomerContactSort.PhoneNumber,
            i18n.t("app.clients.list.header.phone"),
            "panel-clients-sort-phone",
            STYLES.sortButtonPhone,
          )}
        </TableCell>
        <TableCell className={STYLES.headerCellRight}>
          {renderSortButton(
            CustomerContactSort.ReservationsCount,
            i18n.t("app.clients.list.header.reservations_total"),
            "panel-clients-sort-reservations",
            STYLES.sortButtonRight,
          )}
        </TableCell>
        <TableCell className={STYLES.headerCellRight}>
          {renderSortButton(
            CustomerContactSort.MissedReservationsCount,
            i18n.t("app.clients.list.header.reservations_missed"),
            "panel-clients-sort-missed",
            STYLES.sortButtonRight,
          )}
        </TableCell>
      </TableRow>
    </TableHead>
  )
}

type Props = {
  loading: boolean
  contacts: Contact[] | undefined
  sortBy: CustomerContactSort | null
  sortDirection: SortDirection
  onSort: (column: CustomerContactSort) => void
  onContactOpen: (contact: Contact) => void
}

const ClientsTable: React.FC<Props> = ({ loading, contacts, sortBy, sortDirection, onSort, onContactOpen }) => (
  <div className={STYLES.tableWrapper}>
    <Table data-testid="panel-clients-table">
      <TableHeader sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />

      <TableBody>
        {loading &&
          Array.from({ length: 10 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell className={STYLES.skeletonAvatarCell}>
                <Skeleton variant="circular" className={STYLES.skeletonAvatar} />
              </TableCell>
              <TableCell className={STYLES.skeletonNameCell}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="50%" />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="130px" />
              </TableCell>
              <TableCell className={STYLES.cellRight}>
                <div className={STYLES.skeletonRightWrapper}>
                  <Skeleton variant="text" width="30px" />
                </div>
              </TableCell>
              <TableCell className={STYLES.cellRight}>
                <div className={STYLES.skeletonRightWrapper}>
                  <Skeleton variant="text" width="30px" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        {!loading &&
          contacts &&
          contacts.map((contact) => (
            <TableRow key={contact.id} data-testid={`panel-clients-row-${contact.id}`} className={STYLES.contactRow}>
              <TableCell className={STYLES.contactAvatarCell}>
                <Avatar src={contact.avatarUrl} />
              </TableCell>
              <TableCell>
                <button
                  type="button"
                  className={STYLES.contactButton}
                  data-testid={`panel-clients-open-${contact.id}`}
                  onClick={() => onContactOpen(contact)}
                >
                  <span className={STYLES.contactName}>{contact.name}</span>
                  <span className={STYLES.contactEmail}>{contact.email}</span>
                </button>
              </TableCell>
              <TableCell>{contact.phoneFormatted}</TableCell>
              <TableCell className={STYLES.cellRight}>{contact.customer.stats.reservations.total}</TableCell>
              <TableCell className={STYLES.cellRight}>
                <MissedReservations
                  total={contact.customer.stats.reservations.total}
                  missed={contact.customer.stats.reservations.noShow + contact.customer.stats.reservations.cancelled}
                />
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </div>
)

export default ClientsTable
